import React from 'react';
import {
  Alert,
  Box,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import {
  ErrorMessage,
  FileUploader,
  PrimaryLiteButton,
  PrimaryLoadingButton,
  PrimaryNumericField,
  PrimaryOutlinedButton,
  PrimarySelect,
  PrimaryTextField,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { IOption } from 'interfaces';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import {
  CertificateTemplate,
  CertificateValidityUnit,
  ICertificate,
} from '../../../../../interface';
import CertificatePreviewModal from '../certificate-preview-modal';
import {
  buildCertificateRenderRequest,
  getCertificatePreviewFrameHeight,
  renderCertificatePreviewHtml,
  sanitizeCertificate,
} from '../utils';
import {
  CERTIFICATE_TEMPLATES,
  getCertificateTemplateMeta,
} from '../template-catalog';
import {
  CERTIFICATE_LOGO_LIMIT_MESSAGE,
  CERTIFICATE_SIGNATORY_LIMIT_MESSAGE,
  certificateFieldConfigs,
  MAX_CERTIFICATE_LOGOS,
  MAX_CERTIFICATE_SIGNATORIES,
} from '../validation/certificate-modal-form-validation';

interface CertificateModalFormProps {
  open: boolean;
  title: string,
  onSubmit: SubmitHandler<ICertificate>;
  closeModal: () => void;
  loading: boolean;
  sessionTitle?: string;
  moduleTitles?: string[];
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

const templateOptions: IOption[] = [
  ...CERTIFICATE_TEMPLATES.map((template, index) => ({
    id: index + 1,
    value: template.value,
    text: `${template.shortLabel} - ${template.label}`,
  })),
];

const validityUnitOptions: IOption[] = [
  { id: 1, value: CertificateValidityUnit.MONTH, text: 'Month' },
  { id: 2, value: CertificateValidityUnit.YEAR, text: 'Year' },
];

const CertificateModalForm = ({
  open,
  title,
  onSubmit,
  closeModal,
  loading,
  sessionTitle,
  moduleTitles,
  enterprise,
}: CertificateModalFormProps) => {
  const {
    control,
    handleSubmit,
    clearErrors,
    getValues,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ICertificate>();

  const { fields: logoFields, append: appendLogo, remove: removeLogo } = useFieldArray({
    name: 'logos',
    control,
  });
  const {
    fields: signatoryFields,
    append: appendSignatory,
    remove: removeSignatory,
  } = useFieldArray({
    name: 'signatories',
    control,
  });

  const certificateBadge = watch('badge');
  const validity = watch('validity');
  const selectedTemplate = watch('template');
  const previewCertificate = watch();
  const selectedTemplateMeta = getCertificateTemplateMeta(selectedTemplate);
  const logoLimitReached = logoFields.length >= MAX_CERTIFICATE_LOGOS;
  const signatoryLimitReached = signatoryFields.length >= MAX_CERTIFICATE_SIGNATORIES;
  const [openPreviewModal, setOpenPreviewModal] = React.useState(false);

  const sanitizedPreviewCertificate = React.useMemo(
    () => sanitizeCertificate(previewCertificate),
    [previewCertificate],
  );
  const templatePreviewCertificate = React.useMemo(
    (): ICertificate => sanitizeCertificate({
      title: 'Certificate of Completion',
      description: selectedTemplateMeta.description,
      template: selectedTemplate || CertificateTemplate.CERT_TEMPLATE_1,
      header: selectedTemplateMeta.label,
      logos: [],
      badge: undefined,
      signatories: [],
      validity: undefined,
    }),
    [selectedTemplate, selectedTemplateMeta.description, selectedTemplateMeta.label],
  );
  const templatePreviewRequest = React.useMemo(
    () => buildCertificateRenderRequest({
      certificate: templatePreviewCertificate,
      enterprise,
      sessionTitle,
      moduleTitles,
    }),
    [enterprise, moduleTitles, sessionTitle, templatePreviewCertificate],
  );
  const templatePreviewFrameHeight = getCertificatePreviewFrameHeight(selectedTemplate);
  const templatePreviewResult = React.useMemo(() => {
    if (!open) {
      return { html: '', error: null as string | null };
    }

    try {
      return {
        html: renderCertificatePreviewHtml(templatePreviewRequest),
        error: null,
      };
    } catch (error) {
      return {
        html: '',
        error: error instanceof Error ? error.message : 'Failed to load template preview',
      };
    }
  }, [open, templatePreviewRequest]);

  const handleForm = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(onSubmit)();
  };

  const handleUpload = (
    fieldPath: `logos.${number}.logoUrl` | 'badge.imageUrl' | `signatories.${number}.signatureImageUrl`,
  ) => (res: UploadResponse) => {
    clearErrors(fieldPath);
    setValue(fieldPath, res.url, { shouldDirty: true, shouldValidate: true });
    if (res.statusCode !== 201) {
      setError(fieldPath, { type: 'custom', message: res.message });
    }
  };

  const handleRemoveUpload = (
    fieldPath: `logos.${number}.logoUrl` | 'badge.imageUrl' | `signatories.${number}.signatureImageUrl`,
  ) => () => {
    setValue(fieldPath, '', { shouldDirty: true, shouldValidate: true });
  };

  const handleAddLogo = () => {
    if (logoLimitReached) {
      return;
    }
    appendLogo({ title: '', logoUrl: '' });
  };

  const handleAddSignatory = () => {
    if (signatoryLimitReached) {
      return;
    }
    appendSignatory({ name: '', designation: '', signatureImageUrl: '' });
  };

  return (
    <form onSubmit={handleForm}>
      <Typography variant="h4" className="form-item">
        {title}
      </Typography>

      <div className="form-item">
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} label="Certificate title" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.title} />
              </Box>
            </>
          )}
        />
      </div>

      <div className="form-item">
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }) => (
            <>
              <PrimaryTextField {...field} multiline rows={4} label="Certificate description" />
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
              }}
              >
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.description} />
              </Box>
            </>
          )}
        />
      </div>

      <Grid container spacing={2} className="form-item">
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            name="template"
            render={({ field, fieldState }) => (
              <>
                <PrimarySelect {...field} label="Certificate template" options={templateOptions} placeholder="Select template" />
                <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
              </>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            name="header"
            render={({ field, fieldState }) => (
              <>
                <PrimaryTextField {...field} label="Certificate header" />
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 0.5,
                }}
                >
                  <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                  <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.header} />
                </Box>
              </>
            )}
          />
        </Grid>
      </Grid>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate logos</Typography>
          </Grid>
          <Grid item>
            <PrimaryOutlinedButton
              disabled={logoLimitReached}
              startIcon={<AddIcon />}
              onClick={handleAddLogo}
            >
              Add logo
            </PrimaryOutlinedButton>
          </Grid>
        </Grid>
        <ErrorMessage>
          {(errors?.logos as { message?: string } | undefined)?.message
            || (logoLimitReached ? CERTIFICATE_LOGO_LIMIT_MESSAGE : '')}
        </ErrorMessage>

        {logoFields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              mb: 3,
              border: '1px solid #E5E5E5',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="h4">
                  Logo #
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => removeLogo(index)} aria-label={`remove-logo-${index}`}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Logo image</Typography>
              <FileUploader
                handleFile={handleUpload(`logos.${index}.logoUrl`)}
                handleRemove={handleRemoveUpload(`logos.${index}.logoUrl`)}
                currentContentUrl={getValues(`logos.${index}.logoUrl`) || ''}
                scenario={UploadScenarios.sessionCertificateLogo}
                currentMediaType="image"
              />
              <ErrorMessage>{errors?.logos?.[index]?.logoUrl?.message}</ErrorMessage>
            </Box>

            <Controller
              control={control}
              name={`logos.${index}.title`}
              render={({ field: logoField, fieldState }) => (
                <>
                  <PrimaryTextField {...logoField} label="Logo title" />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.5,
                  }}
                  >
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                    <CharacterCountDisplay value={logoField.value || ''} config={certificateFieldConfigs.logoTitle} />
                  </Box>
                </>
              )}
            />
          </Box>
        ))}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate badge</Typography>
          </Grid>
          <Grid item>
            {certificateBadge ? (
              <PrimaryOutlinedButton
                startIcon={<CloseIcon />}
                onClick={() => {
                  setValue('badge', undefined, { shouldDirty: true, shouldValidate: false });
                  clearErrors('badge');
                }}
              >
                Remove badge
              </PrimaryOutlinedButton>
            ) : (
              <PrimaryOutlinedButton
                startIcon={<AddIcon />}
                onClick={() => setValue('badge', { title: '', description: '', imageUrl: '' }, { shouldDirty: true })}
              >
                Add badge
              </PrimaryOutlinedButton>
            )}
          </Grid>
        </Grid>

        {certificateBadge && (
          <Box sx={{ border: '1px solid #E5E5E5', borderRadius: '10px', p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Badge image</Typography>
              <FileUploader
                handleFile={handleUpload('badge.imageUrl')}
                handleRemove={handleRemoveUpload('badge.imageUrl')}
                currentContentUrl={getValues('badge.imageUrl') || ''}
                scenario={UploadScenarios.sessionCertificateBadge}
                currentMediaType="image"
              />
              <ErrorMessage>{errors?.badge?.imageUrl?.message}</ErrorMessage>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Controller
                control={control}
                name="badge.title"
                render={({ field, fieldState }) => (
                  <>
                    <PrimaryTextField {...field} label="Badge title" />
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 0.5,
                    }}
                    >
                      <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                      <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.badgeTitle} />
                    </Box>
                  </>
                )}
              />
            </Box>

            <Controller
              control={control}
              name="badge.description"
              render={({ field, fieldState }) => (
                <>
                  <PrimaryTextField {...field} multiline rows={3} label="Badge description" />
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0.5,
                  }}
                  >
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                    <CharacterCountDisplay value={field.value || ''} config={certificateFieldConfigs.badgeDescription} />
                  </Box>
                </>
              )}
            />
          </Box>
        )}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate signatories</Typography>
          </Grid>
          <Grid item>
            <PrimaryOutlinedButton
              disabled={signatoryLimitReached}
              startIcon={<AddIcon />}
              onClick={handleAddSignatory}
            >
              Add signatory
            </PrimaryOutlinedButton>
          </Grid>
        </Grid>
        <ErrorMessage>
          {(errors?.signatories as { message?: string } | undefined)?.message
            || (signatoryLimitReached ? CERTIFICATE_SIGNATORY_LIMIT_MESSAGE : '')}
        </ErrorMessage>

        {signatoryFields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              mb: 3,
              border: '1px solid #E5E5E5',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Grid item>
                <Typography variant="h4">
                  Signatory #
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => removeSignatory(index)} aria-label={`remove-signatory-${index}`}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h4">Signature image</Typography>
              <FileUploader
                handleFile={handleUpload(`signatories.${index}.signatureImageUrl`)}
                handleRemove={handleRemoveUpload(`signatories.${index}.signatureImageUrl`)}
                currentContentUrl={getValues(`signatories.${index}.signatureImageUrl`) || ''}
                scenario={UploadScenarios.sessionCertificateSignature}
                currentMediaType="image"
              />
              <ErrorMessage>
                {errors?.signatories?.[index]?.signatureImageUrl?.message}
              </ErrorMessage>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name={`signatories.${index}.name`}
                  render={({ field: signatoryField, fieldState }) => (
                    <>
                      <PrimaryTextField {...signatoryField} label="Name" />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                        <CharacterCountDisplay value={signatoryField.value || ''} config={certificateFieldConfigs.signatoryName} />
                      </Box>
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name={`signatories.${index}.designation`}
                  render={({ field: designationField, fieldState }) => (
                    <>
                      <PrimaryTextField {...designationField} label="Designation" />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                        <CharacterCountDisplay value={designationField.value || ''} config={certificateFieldConfigs.signatoryDesignation} />
                      </Box>
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </div>

      <div className="form-item">
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h4">Certificate validity</Typography>
          </Grid>
          <Grid item>
            {validity ? (
              <PrimaryOutlinedButton
                startIcon={<CloseIcon />}
                onClick={() => {
                  setValue('validity', undefined, { shouldDirty: true, shouldValidate: false });
                  clearErrors('validity');
                }}
              >
                Remove validity
              </PrimaryOutlinedButton>
            ) : (
              <PrimaryOutlinedButton
                startIcon={<AddIcon />}
                onClick={() => setValue('validity', { value: 1, unit: CertificateValidityUnit.MONTH }, { shouldDirty: true })}
              >
                Add validity
              </PrimaryOutlinedButton>
            )}
          </Grid>
        </Grid>

        {validity && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="validity.value"
                render={({ field, fieldState }) => (
                  <>
                    <PrimaryNumericField {...field} label="Validity value" min={1} />
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="validity.unit"
                render={({ field, fieldState }) => (
                  <>
                    <PrimarySelect {...field} label="Validity unit" options={validityUnitOptions} placeholder="Select validity unit" />
                    <ErrorMessage>{fieldState.error?.message || ''}</ErrorMessage>
                  </>
                )}
              />
            </Grid>
          </Grid>
        )}
      </div>

      <div className="form-item">
        <Typography variant="h4" sx={{ mb: 1.5 }}>Template preview</Typography>
        <Box
          sx={{
            border: '1px solid #d9e2ec',
            borderRadius: '12px',
            padding: '16px',
            background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '16px',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h4">{selectedTemplateMeta.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTemplateMeta.description}
              </Typography>
            </Box>
            <PrimaryOutlinedButton type="button" onClick={() => setOpenPreviewModal(true)}>
              Preview certificate
            </PrimaryOutlinedButton>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            {templatePreviewResult.error && (
              <Box
                sx={{
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  border: '1px dashed #d9e2ec',
                  borderRadius: '12px',
                  background: 'linear-gradient(180deg, #fff 0%, #f8fbff 100%)',
                }}
              >
                <Alert severity="warning" sx={{ width: '100%' }}>
                  {templatePreviewResult.error}
                </Alert>
              </Box>
            )}

            {!templatePreviewResult.error && templatePreviewResult.html && (
              <Box
                sx={{
                  height: '320px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  borderRadius: '14px',
                  border: '1px solid #d9e2ec',
                  background: '#e9eef5',
                  boxShadow: 'inset 0 1px 0 rgb(255 255 255 / 65%)',
                  padding: '16px 12px',
                }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    width: `${Math.round(1122 * 0.26)}px`,
                    height: `${Math.round(templatePreviewFrameHeight * 0.26)}px`,
                  }}
                >
                  <iframe
                    title={`${selectedTemplateMeta.label} thumbnail preview`}
                    srcDoc={templatePreviewResult.html}
                    style={{
                      width: '1122px',
                      height: `${templatePreviewFrameHeight}px`,
                      border: 0,
                      transform: 'scale(0.26)',
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                      background: '#fff',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            This thumbnail uses sample HTML only. The full preview
            uses your current form data and PDF download.
          </Typography>
        </Box>
      </div>

      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLiteButton fullWidth onClick={closeModal}>Cancel</PrimaryLiteButton>
        </Grid>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} fullWidth type="submit">
            Add & continue
          </PrimaryLoadingButton>
        </Grid>
      </Grid>

      <CertificatePreviewModal
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        certificate={sanitizedPreviewCertificate}
        sessionTitle={sessionTitle}
        moduleTitles={moduleTitles}
        enterprise={enterprise}
      />
    </form>
  );
};

CertificateModalForm.defaultProps = {
  sessionTitle: '',
  moduleTitles: [],
  enterprise: null,
};

export default CertificateModalForm;
